/**
 * Langes Drücken = Rechtsklick (01.11.2026).
 *
 * Fast alle Kontextmenüs in EasyWork hängen an `onContextMenu`. iOS Safari löst dieses Ereignis
 * beim langen Drücken nie aus, Android nur uneinheitlich – auf dem Handy waren die Menüs daher
 * unerreichbar. Diese zentrale Nachrüstung erkennt langes Drücken (ca. 0,5 s, Finger bleibt
 * ruhig) und schickt an genau dieser Stelle ein `contextmenu`-Ereignis mit den Fingerkoordinaten.
 * Damit öffnen alle bestehenden Rechtsklick-Menüs ohne Änderung an den Komponenten.
 *
 * - Nur bei Touch-Bedienung (pointerType „touch“), Maus und Stift bleiben unberührt.
 * - Eingabefelder, Textbereiche und editierbare Inhalte behalten das Menü des Systems
 *   (Kopieren, Einfügen, Textauswahl); Drag-&-Drop-Griffe und Elemente mit
 *   `data-kein-langdruck` sind ausgenommen.
 * - Öffnet eine Komponente ihr Menü (preventDefault), wird der anschließende „Klick“ beim
 *   Loslassen verschluckt, damit nicht zusätzlich das Element geöffnet wird; kurze Vibration
 *   als Rückmeldung, wo das Gerät es kann.
 * - Löst der Browser selbst schon ein contextmenu aus (Android), wird es nicht doppelt geöffnet.
 */

const HALTEZEIT_MS = 500;
const TOLERANZ_PX = 10;

// Eingaben behalten das System-Menü; Drag-&-Drop-Griffe (@hello-pangea/dnd, z. B. Lead-Karten)
// verschieben beim Halten – dort hätten Menü und Ziehen gleichzeitig gestartet. Diese Karten
// haben ihr Menü zusätzlich im ⋮-Knopf.
const istAusgenommen = (el) =>
  !!el?.closest?.('input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-rfd-drag-handle-draggable-id], [data-kein-langdruck]');

export function installLangDruecken() {
  if (typeof window === 'undefined' || window.__ewLangDruecken) return;
  window.__ewLangDruecken = true;

  let timer = null;
  let start = null; // { x, y, ziel, id }
  let ausgeloest = false; // unser contextmenu ist in dieser Berührung bereits gelaufen
  let klickSchlucken = false;

  const abbrechen = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    start = null;
  };

  const ausloesen = () => {
    timer = null;
    if (!start) return;
    const { x, y, ziel } = start;
    ausgeloest = true;
    const ev = new MouseEvent('contextmenu', {
      bubbles: true, cancelable: true, composed: true, view: window,
      clientX: x, clientY: y, screenX: x, screenY: y, button: 2, buttons: 0,
    });
    ev.__ewLangDruck = true;
    const zielEl = document.contains(ziel) ? ziel : document.elementFromPoint(x, y);
    if (!zielEl) return;
    zielEl.dispatchEvent(ev);
    if (ev.defaultPrevented) {
      // Ein Menü hat sich geöffnet: Klick beim Loslassen und Textauswahl verhindern
      klickSchlucken = true;
      try { window.getSelection()?.removeAllRanges(); } catch { /* egal */ }
      try { navigator.vibrate?.(12); } catch { /* egal */ }
    }
  };

  window.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch' || !e.isPrimary) { abbrechen(); return; }
    ausgeloest = false;
    klickSchlucken = false;
    if (istAusgenommen(e.target)) { abbrechen(); return; }
    abbrechen();
    start = { x: e.clientX, y: e.clientY, ziel: e.target, id: e.pointerId };
    timer = setTimeout(ausloesen, HALTEZEIT_MS);
  }, { capture: true, passive: true });

  window.addEventListener('pointermove', (e) => {
    if (!start || e.pointerId !== start.id) return;
    if (Math.abs(e.clientX - start.x) > TOLERANZ_PX || Math.abs(e.clientY - start.y) > TOLERANZ_PX) abbrechen();
  }, { capture: true, passive: true });

  // Zweiter Finger, Scrollen oder Loslassen beendet das Warten
  window.addEventListener('pointerup', abbrechen, { capture: true, passive: true });
  window.addEventListener('pointercancel', abbrechen, { capture: true, passive: true });
  window.addEventListener('scroll', abbrechen, { capture: true, passive: true });

  // Beim Loslassen nach geöffnetem Menü: Klick (und iOS-Doppelauslösung) verschlucken
  const schlucken = (e) => {
    if (!klickSchlucken) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'click') klickSchlucken = false;
  };
  window.addEventListener('touchend', (e) => { if (klickSchlucken && e.cancelable) e.preventDefault(); }, { capture: true, passive: false });
  window.addEventListener('click', schlucken, { capture: true });

  // Eigenes contextmenu des Browsers (Android) – nicht doppelt öffnen
  window.addEventListener('contextmenu', (e) => {
    if (e.__ewLangDruck) return;
    if (ausgeloest) {
      // Wir haben in dieser Berührung schon ausgelöst → das Browser-Ereignis verwerfen
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    // Browser war schneller: unseren Timer stoppen, sein Ereignis läuft normal weiter
    if (timer) { abbrechen(); ausgeloest = true; }
  }, { capture: true });
}

/** true auf Geräten ohne Maus (Handy, Tablet). */
export const istTouchGeraet = () =>
  typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(hover: none)').matches;

/**
 * Bedienhinweis je nach Gerät: `rk('Rechtsklick für Optionen', 'Lange drücken für Optionen')`.
 */
export const rk = (maus, touch) => (istTouchGeraet() ? touch : maus);
